import { Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { complianceReports } from 'src/api/oApi';
import { ConfirmDialog } from 'src/components/dialogs/ConfirmDialog';
import { Colors } from 'src/components/utils/Theme';
import { downloadBase64Pdf } from 'src/helpers/downloadFile';
import { generalStore } from 'src/stores/GeneralStore';

type ReportAction = 'exPostUser' | 'exPostAll' | 'taxReportUser' | 'taxReportAll';

const ALL_USERS_WARNING =
  'This mails every affected user their document. It cannot be undone and it runs in the background on the server — starting it twice sends the documents twice.';

const errorMessage = (error: any) =>
  error?.response?.data?.error?.message || error?.response?.data?.error || error?.message || 'Could not generate the report';

const ReportSection: FC<{ title: string; description: string; children: React.ReactNode }> = ({ title, description, children }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
    <Typography variant="subtitle1" style={{ fontFamily: 'Aestetico-semibold', color: Colors.BLACK_BLUE }}>
      {title}
    </Typography>
    <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
      {description}
    </Typography>
    <Stack direction="row" spacing={2}>
      {children}
    </Stack>
  </Paper>
);

export const ReportsSite = () => {
  const [year, setYear] = useState(String(new Date().getFullYear() - 1));
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState<ReportAction | null>(null);

  const parsedYear = Number(year);
  const isYearValid = /^\d{4}$/.test(year) && parsedYear >= 2019 && parsedYear <= new Date().getFullYear();
  const canRunForUser = isYearValid && userId.trim().length > 0;

  const dialogs: Record<
    ReportAction,
    { title: string; content: string; warning?: string; confirmLabel: string; run: () => Promise<void> }
  > = {
    exPostUser: {
      title: `Ex-post cost information for ${userId.trim()}`,
      content: `Generate the ex-post cost information (Kosteninformation) for user ${userId.trim()} and the year ${parsedYear}. The PDF is downloaded here and also stored on the server.`,
      confirmLabel: 'Generate',
      run: async () => {
        const response = await complianceReports.exPost.forUser({ userId: userId.trim(), year: parsedYear });
        const pdf = response.data.data?.pdf;
        if (!pdf) throw new Error('The backend did not return a PDF for this user and year');
        downloadBase64Pdf(pdf, `Kosteninformation_${userId.trim()}_${parsedYear}`);
        generalStore.setMessage(`Ex-post cost information for ${userId.trim()} (${parsedYear}) generated`);
      },
    },
    exPostAll: {
      title: `Ex-post cost information for ALL users (${parsedYear})`,
      content: `Generate the ex-post cost information (Kosteninformation) for every user with tax relevant transactions in ${parsedYear}.`,
      warning: ALL_USERS_WARNING,
      confirmLabel: 'Start for all users',
      run: async () => {
        const response = await complianceReports.exPost.forAllUsers({ year: parsedYear });
        const users = response.data.data?.users;
        generalStore.setMessage(
          `Ex-post run for ${parsedYear} started${typeof users === 'number' ? ` for ${users} users` : ''}. It continues in the background.`,
        );
      },
    },
    taxReportUser: {
      title: `Yearly tax report for ${userId.trim()}`,
      content: `Generate the yearly tax report (Jahresreport) for user ${userId.trim()} and the year ${parsedYear}.`,
      warning: 'This mails the document to that user. It cannot be undone.',
      confirmLabel: 'Generate and send',
      run: async () => {
        await complianceReports.taxReport.forUser({ userId: userId.trim(), year: parsedYear });
        generalStore.setMessage(`Yearly tax report for ${userId.trim()} (${parsedYear}) generated and sent`);
      },
    },
    taxReportAll: {
      title: `Yearly tax report for ALL users (${parsedYear})`,
      content: `Generate the yearly tax report (Jahresreport) for every user with tax relevant transactions in ${parsedYear}.`,
      warning: ALL_USERS_WARNING,
      confirmLabel: 'Start for all users',
      run: async () => {
        await complianceReports.taxReport.forAllUsers({ year: parsedYear });
        generalStore.setMessage(`Yearly tax report run for ${parsedYear} started. It continues in the background.`);
      },
    },
  };

  const activeDialog = action ? dialogs[action] : null;

  return (
    <>
      <Typography variant="h5" style={{ fontFamily: 'Aestetico-semibold', color: Colors.BLACK_BLUE }} sx={{ mb: 2 }}>
        Compliance Reports
      </Typography>

      <Box sx={{ maxWidth: 900 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <TextField
            label="Year"
            value={year}
            size="small"
            error={year.length > 0 && !isYearValid}
            helperText={year.length > 0 && !isYearValid ? 'Four digit year, 2019 up to the current year' : ' '}
            onChange={(event) => setYear(event.target.value.trim())}
          />
          <TextField
            label="UserId"
            value={userId}
            size="small"
            sx={{ minWidth: 320 }}
            helperText="Only needed for the single user reports"
            onChange={(event) => setUserId(event.target.value)}
          />
        </Stack>

        <ReportSection
          title="Ex-post cost information (Kosteninformation)"
          description="MiFID ex-post cost disclosure for the selected year. The single user report is only downloaded here, the all users run mails every affected user."
        >
          <Button variant="contained" disableElevation disabled={!canRunForUser} onClick={() => setAction('exPostUser')}>
            For one user
          </Button>
          <Button variant="outlined" color="warning" disabled={!isYearValid} onClick={() => setAction('exPostAll')}>
            For all users
          </Button>
        </ReportSection>

        <ReportSection
          title="Yearly tax report (Jahresreport)"
          description="Yearly tax report covering MEG and debt capital products. Both variants mail the document to the affected users."
        >
          <Button variant="contained" disableElevation disabled={!canRunForUser} onClick={() => setAction('taxReportUser')}>
            For one user
          </Button>
          <Button variant="outlined" color="warning" disabled={!isYearValid} onClick={() => setAction('taxReportAll')}>
            For all users
          </Button>
        </ReportSection>
      </Box>

      {activeDialog && (
        <ConfirmDialog
          open={true}
          title={activeDialog.title}
          content={activeDialog.content}
          warning={activeDialog.warning}
          confirmLabel={activeDialog.confirmLabel}
          onClose={() => setAction(null)}
          onConfirm={async () => {
            generalStore.setIsLoading(true);
            try {
              await activeDialog.run();
            } catch (error: any) {
              generalStore.setError(errorMessage(error), error);
            } finally {
              generalStore.setIsLoading(false);
            }
          }}
        />
      )}
    </>
  );
};
